import React, { useState } from 'react';
import { BarChart2, Sparkles, Copy, Loader2, Mail } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import VisualizationRenderer from '../components/charts/VisualizationRenderer';
import type { Visualization } from '../components/charts/VisualizationRenderer';
import SendReportModal from '../components/reports/SendReportModal';
import { reportsAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';

const PRESET_REPORTS = [
  { title: 'Weekly Performance Summary', query: 'Give me a summary of this week\'s AI performance' },
  { title: 'Escalation Analysis', query: 'Why are customers escalating and what are the top reasons?' },
  { title: 'CSAT Breakdown', query: 'Analyze customer satisfaction scores and trends' },
  { title: 'Channel Performance', query: 'Compare chat vs email channel performance and resolution rates' },
  { title: 'Knowledge Gaps', query: 'What topics are customers asking about that our knowledge base doesn\'t cover?' },
];

const Reports: React.FC = () => {
  const { showToast } = useToast();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<string | null>(null);
  const [visualizations, setVisualizations] = useState<Visualization[]>([]);

  // Everything else about sending — recipients, search, validation, the
  // request itself — now lives in `components/reports/SendReportModal`, shared
  // with the Orchestrator. This page only owns whether it is open.
  const [showSendModal, setShowSendModal] = useState(false);

  const generateReport = async (q: string) => {
    if (!q.trim()) {
      showToast('Please enter a query to generate a report.', 'error');
      return;
    }
    setLoading(true);
    setReport(null);
    setVisualizations([]);
    try {
      const res = await reportsAPI.generate(q);
      setReport(res.data.report || res.data.narrative || JSON.stringify(res.data));
      setVisualizations(res.data.visualizations || []);
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Failed to generate report. Please try again.';
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePreset = (preset: typeof PRESET_REPORTS[number]) => {
    setQuery(preset.query);
    generateReport(preset.query);
  };

  const copyReport = () => {
    if (report) {
      navigator.clipboard.writeText(report);
      showToast('Report copied to clipboard.', 'success');
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold gradient-text">Reports</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Ask anything about your support data — powered by AskProdigy AI analytics.
        </p>
      </div>

      {/* Query Bar */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !loading && generateReport(query)}
            placeholder="Ask anything about your support data..."
            className="input-field flex-1"
            disabled={loading}
          />
          <button
            onClick={() => generateReport(query)}
            disabled={loading || !query.trim()}
            className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap px-6"
          >
            <Sparkles className="w-4 h-4" />
            Generate Report
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="card p-8 flex flex-col items-center justify-center text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mb-3" />
          <p className="text-gray-700 dark:text-gray-300 font-medium">Analyzing your data...</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">This may take a moment.</p>
        </div>
      )}

      {/* Report Output */}
      {report && !loading && (
        <div className="card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">AI Analytics Report</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSendModal(true)}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Send as Email
              </button>
              <button
                onClick={copyReport}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                <Copy className="w-4 h-4" />
                Copy Report
              </button>
            </div>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="prose dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {report}
              </ReactMarkdown>
            </div>
          </div>
          {visualizations.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                <BarChart2 className="w-3.5 h-3.5" />
                Data Visualizations
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {visualizations.map((viz, i) => (
                  <div key={i} className={viz.type === 'table' || (viz.type === 'line_chart' && (viz.data?.length ?? 0) > 8) ? 'lg:col-span-2' : ''}>
                    <VisualizationRenderer viz={viz} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preset Report Cards */}
      {!loading && !report && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Quick Reports</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PRESET_REPORTS.map((preset) => (
              <button
                key={preset.title}
                onClick={() => handlePreset(preset)}
                className="card card-hover p-4 text-left transition-all duration-200 hover:scale-[1.02]"
              >
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-1">{preset.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{preset.query}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      <SendReportModal
        open={showSendModal}
        onClose={() => setShowSendModal(false)}
        report={report ?? ''}
        query={query}
        visualizations={visualizations}
      />
    </div>
  );
};

export default Reports;
